/* ─────────────────────────────  src/utils/strandsClient.js  ────────────────────────────
   Cliente para conectarse al backend de Strands Agent (Claude via Bedrock)
   Maneja streaming SSE y requests normales
   ───────────────────────────────────────────────────────────────────────────────────── */

// URL del backend Strands - configurar en .env
const STRANDS_API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';

/**
 * Envia una consulta al agente Strands y recibe la respuesta completa
 * @param {Object} opts - Opciones de la consulta
 * @param {string} opts.query - Pregunta/consulta del usuario
 * @param {string} [opts.username] - Nombre del usuario
 * @param {Array} [opts.conversationHistory] - Historial de conversacion
 * @param {Object} [opts.fileData] - Datos del archivo (excel_data, pdf_data, etc.)
 * @returns {Promise<Object>} - Respuesta del agente
 */
export async function queryAgent({
  query,
  username = 'anonymous',
  conversationHistory = [],
  fileData = {}
}) {
  try {
    const response = await fetch(`${STRANDS_API_URL}/query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query,
        username,
        conversation_history: conversationHistory,
        ...fileData
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('[StrandsClient] Query error:', error);
    throw error;
  }
}

/**
 * Envia una consulta al agente Strands con streaming SSE
 * @param {Object} opts - Opciones de la consulta
 * @param {string} opts.query - Pregunta/consulta del usuario
 * @param {string} [opts.username] - Nombre del usuario
 * @param {Array} [opts.conversationHistory] - Historial de conversacion
 * @param {Object} [opts.fileData] - Datos del archivo
 * @param {Function} opts.onChunk - Callback para cada chunk de texto
 * @param {Function} [opts.onToolUse] - Callback cuando se usa una herramienta
 * @param {Function} [opts.onStart] - Callback al iniciar
 * @param {Function} [opts.onDone] - Callback al terminar
 * @param {Function} [opts.onError] - Callback en caso de error
 * @returns {Promise<void>}
 */
export async function streamQuery({
  query,
  username = 'anonymous',
  conversationHistory = [],
  fileData = {},
  onChunk,
  onToolUse,
  onStart,
  onDone,
  onError
}) {
  try {
    const response = await fetch(`${STRANDS_API_URL}/query-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query,
        username,
        conversation_history: conversationHistory,
        ...fileData
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullResponse = '';

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Procesar eventos SSE
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Guardar linea incompleta

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));

            switch (data.type) {
              case 'start':
                if (onStart) onStart(data);
                break;

              case 'chunk':
                fullResponse += data.content;
                if (onChunk) onChunk(data.content, fullResponse);
                break;

              case 'tool_use':
                if (onToolUse) onToolUse(data.tool);
                break;

              case 'thinking_start':
              case 'thinking':
              case 'thinking_stop':
                // Eventos de pensamiento (Extended Thinking)
                if (onChunk) onChunk('', fullResponse, data);
                break;

              case 'done':
                if (onDone) onDone(data.full_response || fullResponse);
                break;

              case 'error':
                if (onError) onError(new Error(data.message));
                break;

              default:
                console.log('[StrandsClient] Unknown event:', data.type);
            }
          } catch (parseError) {
            console.warn('[StrandsClient] Parse error:', parseError);
          }
        }
      }
    }

    // Si no se recibio evento 'done', llamar callback con respuesta acumulada
    if (fullResponse && onDone) {
      onDone(fullResponse);
    }

  } catch (error) {
    console.error('[StrandsClient] Stream error:', error);
    if (onError) {
      onError(error);
    } else {
      throw error;
    }
  }
}

/**
 * Sube un archivo al backend para procesamiento
 * @param {File} file - Archivo a subir
 * @returns {Promise<Object>} - Contenido procesado del archivo
 */
export async function uploadFile(file) {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${STRANDS_API_URL}/upload-file`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('[StrandsClient] Upload error:', error);
    throw error;
  }
}

/**
 * Verifica el estado del backend
 * @returns {Promise<Object>} - Estado del backend
 */
export async function healthCheck() {
  try {
    const response = await fetch(`${STRANDS_API_URL}/health`);
    return await response.json();
  } catch (error) {
    console.error('[StrandsClient] Health check error:', error);
    return { status: 'unhealthy', error: error.message };
  }
}

/**
 * Convierte un archivo a base64 para enviarlo en el body
 * @param {File} file - Archivo a convertir
 * @returns {Promise<string>} - Contenido en base64
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // Remover el prefijo "data:...;base64,"
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Helper para preparar datos de archivo para enviar al backend
 * @param {File} file - Archivo a preparar
 * @returns {Promise<Object>} - Objeto con datos del archivo
 */
export async function prepareFileData(file) {
  const base64 = await fileToBase64(file);
  const filename = file.name;

  if (filename.endsWith('.xlsx') || filename.endsWith('.xls')) {
    return {
      excel_data: base64,
      excel_filename: filename
    };
  } else if (filename.endsWith('.pdf')) {
    return {
      pdf_data: base64,
      pdf_filename: filename
    };
  } else if (file.type.startsWith('image/')) {
    return {
      image_data: base64,
      image_filename: filename,
      image_type: file.type
    };
  }

  throw new Error(`Tipo de archivo no soportado: ${filename}`);
}

// ============================================================================
// FUNCIONES DE VISUALIZACION DE DATOS
// ============================================================================

/**
 * Ejecuta SQL directamente en Athena
 * @param {string} sql - Query SQL a ejecutar
 * @param {number} [maxRows] - Limite opcional de filas
 * @returns {Promise<Object>} - { success, data, metadata }
 */
export async function executeSQLDirect(sql, maxRows = null, username = '') {
  // Timeout de 120 segundos (2 minutos) para queries SQL en Athena
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 120000);

  try {
    const body = { sql };
    if (maxRows !== null) {
      body.max_rows = maxRows;
    }
    if (username) {
      body.username = username;
    }

    const response = await fetch(`${STRANDS_API_URL}/execute-sql`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }

    return result;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      console.error('[StrandsClient] Execute SQL timeout');
      throw new Error('Timeout: La consulta tardó demasiado');
    }
    console.error('[StrandsClient] Execute SQL error:', error);
    throw error;
  }
}

/**
 * Genera SQL expandido (sin LIMIT) desde la respuesta del agente
 * @param {string} agentResponse - Respuesta del agente con datos
 * @param {string} originalQuery - Pregunta original del usuario
 * @returns {Promise<Object>} - { success, sql, explanation }
 */
export async function expandSQL(agentResponse, originalQuery) {
  try {
    const response = await fetch(`${STRANDS_API_URL}/expand-sql`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        agent_response: agentResponse,
        original_query: originalQuery
      })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }

    return result;
  } catch (error) {
    console.error('[StrandsClient] Expand SQL error:', error);
    throw error;
  }
}

/**
 * Carga datos de visualizacion completos
 * Flujo: expandSQL -> executeSQLDirect
 * @param {string} agentResponse - Respuesta del agente
 * @param {string} originalQuery - Pregunta original
 * @param {number} [maxRows] - Limite opcional de filas
 * @returns {Promise<Object>} - { data, metadata, sql }
 */
export async function loadVisualizationData(agentResponse, originalQuery, maxRows = null) {
  try {
    // Paso 1: Expandir SQL desde respuesta del agente
    console.log('[StrandsClient] Expandiendo SQL...');
    const expandResult = await expandSQL(agentResponse, originalQuery);

    if (!expandResult.success || !expandResult.sql) {
      throw new Error('No se pudo generar SQL para visualizacion');
    }

    console.log('[StrandsClient] SQL generado:', expandResult.sql.substring(0, 100));

    // Paso 2: Ejecutar SQL
    console.log('[StrandsClient] Ejecutando SQL...');
    const execResult = await executeSQLDirect(expandResult.sql, maxRows);

    if (!execResult.success) {
      throw new Error(execResult.error || 'Error ejecutando SQL');
    }

    console.log(`[StrandsClient] Datos cargados: ${execResult.metadata.total_rows} filas`);

    return {
      data: execResult.data,
      metadata: execResult.metadata,
      sql: expandResult.sql,
      explanation: expandResult.explanation
    };
  } catch (error) {
    console.error('[StrandsClient] Load visualization error:', error);
    throw error;
  }
}

/**
 * Pide a Claude (vía backend) una transformación SQL para graficar una tabla
 * que no es directamente graficable. Devuelve SQL agregado + chartConfig.
 *
 * @param {Object} args
 * @param {string} args.originalQuery   Pregunta original del usuario
 * @param {string} args.currentSql      SQL actual de la tabla mostrada
 * @param {string[]} args.columns       Nombres de columnas
 * @param {Object} args.columnTypes     { col: type } mapa de tipos
 * @param {string} [args.username]
 * @returns {Promise<{success, sql, chartConfig: {labelColumn, valueColumn, chartType, title}}>}
 */
export async function suggestChartTransformation({ originalQuery, currentSql, sampleRows, columns, columnTypes, username = '' }) {
  try {
    const response = await fetch(`${STRANDS_API_URL}/api/chart/suggest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        original_query: originalQuery,
        current_sql: currentSql,
        sample_rows: sampleRows,
        columns,
        column_types: columnTypes,
        username,
      })
    });
    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }
    return result;
  } catch (error) {
    console.error('[StrandsClient] Chart suggest error:', error);
    throw error;
  }
}

// Export default con todos los metodos
export default {
  queryAgent,
  streamQuery,
  uploadFile,
  healthCheck,
  fileToBase64,
  prepareFileData,
  executeSQLDirect,
  expandSQL,
  loadVisualizationData
};
